import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { uploadFile } from 'actions/admin';
import { setShowSuccess, setPickerFilters, toggleFormHelp } from 'actions/ui';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import { ModalPicker } from 'components/user/modalPicker';
import { apiCode, apiConfig, apiGet, apiPatch, apiPost } from 'services';
import { Loading, Modal, SaveButton, SystemText } from '@salesperformanceint/e4shared';
import config from 'config';
import Upload from './components/upload';

export const AdminLanguages = () => {
    const dispatch = useDispatch();
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [showPicker, setShowPicker] = React.useState(false);
    const [showUpload, setShowUpload] = React.useState('');
    const [pickerItem, setPickerItem] = React.useState({});
    const [pickerIds, setPickerIds] = React.useState([]);
    const [pickerDisabledUsers, setPickerDisabledUsers] = React.useState([]);
    const [fileErrors, setFileErrors] = React.useState([]);
    const [file, setFile] = React.useState(null);
    const [uploadLoading, setUploadLoading] = React.useState(false);

    const user = useSelector(state => state.user.user);
    const company = user.companySettings;

    const api = {
        load: () => {
            setLoading(true);
            apiGet('language', user.guid, response => {
                setData(response.map(x => ({ ...x, activeDisabled: isDisabled(response.length, x) })));
                setLoading(false);
            });
        },
        save: company => {
            apiPatch('language', company, user.guid, () => api.load());
        },
        download: code => {
            fetch(`${config.apiGateway.URL}language-file/${code}${apiCode(true)}`, apiConfig(user.guid, null, true))
                .then(response => {
                    const blob = response.blob();
                    blob.type = response.headers['content-type'];
                    return blob;
                })
                .then(blob => {
                    setLoading(false);
                    let now = new Date();
                    let a = document.createElement('a');
                    document.body.appendChild(a);
                    let url = window.URL.createObjectURL(blob);
                    a.style = 'display: none';
                    a.href = url;
                    a.download = `richardson-insights-translation-${code.toLowerCase()}-${now.toISOString()}.xlsx`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch(err => {
                    // console.log(err);
                });
        },
        saveStaff: (language, callback) => {
            apiPatch('language-staff', language, user.guid, () => {
                callback();
                api.load();
            });
        },
    };

    React.useEffect(() => {
        api.load();
    }, []);

    React.useEffect(() => {
        if (file) {
            file.companyId = user.company_Id;
            let errs = [];
            setFileErrors([]);
            if (file.name.indexOf('.xlsx') !== file.name.length - 5) {
                errs = [SystemText('Admin/Languages/Upload/Errors/InvalidFileType')];
                setFileErrors(errs);
            } else if (file.name.toLowerCase().indexOf(showUpload.toLowerCase()) < 0) {
                errs = [SystemText('Admin/Languages/Upload/Errors/InvalidLanguage', showUpload)];
                setFileErrors(errs);
            } else {
                setUploadLoading(true);
                const packet = {};
                const path = 'reports:translation:upload';
                packet[path] = file;
                uploadFile(packet, path, config, apiConfig(user.guid, true), response => {
                    setTimeout(() => {
                        apiGet(
                            `language-upload/${showUpload}?report=${response?.data?.path}`,
                            user.guid,
                            response => {
                                setUploadLoading(false);

                                // response is a list of errors. In case it's 0, it means no error
                                if (response?.length === 0) {
                                    setShowUpload('');
                                    dispatch(setShowSuccess(SystemText('Admin/Languages/Upload/Success')));
                                    return;
                                }

                                // errors returned from API, so display it
                                setFileErrors(response.map(x => x.item1));
                            },
                            () => {
                                setUploadLoading(false);
                                setFileErrors([SystemText('Admin/Languages/Upload/Errors/Unknown')]);
                            }
                        );
                    }, 1000);
                });
            }
        }
    }, [file]);

    const isActive = l => {
        const company = user.companySettings;
        return data.length <= 1 || (company.language_Id || 1) === l.id || (company.language_Ids || []).some(x => x === l.id);
    };

    const isDefault = l => {
        const company = user.companySettings;
        return data.length <= 1 || (company.language_Id || 1) === l.id;
    };

    const isDisabled = (len, l) => {
        return len <= 1 || (company.language_Id || 1) === l.id;
    };

    // user.companySettings.language_Ids can be a stale, cached-at-login snapshot (e.g. out of date with
    // languages added/removed via the Partners app since login). Deriving language_Ids from the freshly
    // loaded `data` here, rather than trusting that stale array, stops a save from silently reverting the
    // company's active languages to whatever they were when the session started.
    const currentActiveLanguageIds = () => data.filter(l => l.active).map(l => l.id);

    const setLanguageDefault = id => {
        const company = { ...user.companySettings, language_Id: id, language_Ids: currentActiveLanguageIds() };
        api.save(company);
    };

    const setLanguageActive = language => {
        const { id, active, activeDisabled } = language;
        if (!activeDisabled) {
            const language_Ids = currentActiveLanguageIds()
                .filter(x => x !== id)
                .concat(active ? [id] : []);
            const company = { ...user.companySettings, language_Ids };
            api.save(company);
        }
    };

    const options = {
        ...DefaultOptions(),
        label: SystemText('Admin/Languages/Title'),
        labelPlural: SystemText('Admin/Languages/Title/Plural'),
        showDelete: false,
        showActive: true,
        extraOptions: [
            {
                btnClass: d => (isDefault(d) ? 'btn-success' : 'btn-danger'),
                label: d =>
                    isDefault(d)
                        ? SystemText('Admin/Languages/Table/Options/Default')
                        : SystemText('Admin/Languages/Table/Options/NonDefault'),
                icon: 'fa-language',
                disabled: d => isDefault(d) || !isActive(d),
                onClick: d => setLanguageDefault(d.id),
            },
            {
                btnClass: 'btn-info',
                label: SystemText('Admin/Languages/Table/Options/Download'),
                icon: 'fa-cloud-download',
                onClick: d => api.download(d.code),
            },
            {
                btnClass: 'btn-info',
                label: SystemText('Admin/Languages/Table/Options/Upload'),
                icon: 'fa-cloud-upload',
                onClick: d => setShowUpload(d.code),
            },
        ],
    };

    if (!user.support) {
        options.extraOptions = options.extraOptions.filter(x => x.icon === 'fa-language');
    }

    const tableColumns = [
        {
            label: SystemText('Admin/Languages/Table/Columns/Language'),
            dataProperty: 'name',
            renderer: l => <span>{l.name}</span>,
        },
        {
            label: SystemText('Admin/Languages/Table/Columns/Users'),
            dataProperty: 'staff_Ids',
            showCount: true,
            onClick:
                data.length <= 1
                    ? undefined
                    : item => {
                          const staffIds = item.staff_Ids || [];
                          setPickerItem(item);
                          setPickerIds(staffIds);
                          setPickerDisabledUsers(
                              staffIds.map(id => ({
                                  id,
                                  message: SystemText('Admin/Languages/Table/Columns/Users/Picker/DisabledUser'),
                              }))
                          );
                          setShowPicker(true);
                      },
        },
    ];

    const closePicker = () => {
        setPickerItem({});
        setPickerIds([]);
        setPickerDisabledUsers([]);
        setShowPicker(false);
        dispatch(setPickerFilters([]));
    };

    const savePickerIds = () => {
        api.saveStaff({ ...pickerItem, update_Staff_Ids: pickerIds }, () => {
            dispatch(setShowSuccess(SystemText('Admin/Languages/Success/Users')));
            closePicker();
        });
        setSaving(false);
    };

    const pickerFooter = (
        <div>
            <SaveButton
                data-testid="save-button"
                saving={saving}
                setSaving={setSaving}
                className="btn btn-success"
                onClick={savePickerIds}
            >
                {SystemText('General/Buttons/Save')}
            </SaveButton>
            <button data-testid="cancel-button" className="btn" onClick={closePicker}>
                {SystemText('General/Buttons/Cancel')}
            </button>
        </div>
    );

    return (
        <div>
            {loading ? (
                <Loading />
            ) : (
                <SimpleAdmin
                    blockAddNew
                    update={setLanguageActive}
                    data={data}
                    fields={[]}
                    options={options}
                    columns={tableColumns}
                />
            )}
            <Modal
                data-testid="language-users-modal"
                show={showPicker}
                close={closePicker}
                footer={pickerFooter}
                toggleFormHelp={() => dispatch(toggleFormHelp())}
                className="modal-body-full modal-920"
                title={SystemText('Admin/Languages/Table/Columns/Users/Picker/Title', pickerItem?.name)}
                iconsPre
            >
                <ModalPicker
                    value={pickerIds}
                    disabledUsers={pickerDisabledUsers}
                    onChange={setPickerIds}
                    companyWide
                    excludeFields={['behaviour_Score', 'course_Status', 'collectionsAssigned']}
                />
            </Modal>

            <Upload
                show={showUpload}
                onClose={() => {
                    setShowUpload('');
                    setFileErrors([]);
                }}
                file={file}
                setFile={setFile}
                loading={uploadLoading}
                fileErrors={fileErrors}
            />
        </div>
    );
};

export default AdminLanguages;
